import React, { useState, useRef } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  FileUpload,
  Alert,
  ProgressBar,
  Box,
  Modal,
  Grid
} from '@cloudscape-design/components';
import { s3, config, AWS } from '../aws-config';
import { v4 as uuidv4 } from 'uuid';

function DocumentUpload() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [alert, setAlert] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const testCredentials = async () => {
    const debugInfo = {
      envVars: {
        accessKey: process.env.REACT_APP_AWS_ACCESS_KEY_ID ? 'Present' : 'Missing',
        secretKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY ? 'Present' : 'Missing',
        sessionToken: process.env.REACT_APP_AWS_SESSION_TOKEN ? 'Present' : 'Missing'
      },
      awsConfig: {
        accessKeyId: AWS.config.credentials?.accessKeyId || 'Not set',
        region: AWS.config.region || 'Not set'
      }
    };
    
    console.log('=== CREDENTIAL DEBUG ===', debugInfo);
    
    // Show debug info in UI
    setAlert({ 
      type: 'info', 
      content: `Debug: ENV vars - Access: ${debugInfo.envVars.accessKey}, Secret: ${debugInfo.envVars.secretKey}, Token: ${debugInfo.envVars.sessionToken}` 
    });
    
    try {
      const result = await s3.listBuckets().promise();
      console.log('✅ Credentials work!');
      return true;
    } catch (error) {
      console.error('❌ Credential error:', error.code, error.message);
      setAlert({ 
        type: 'error', 
        content: `AWS Error: ${error.code} - ${error.message}` 
      });
      return false;
    }
  };

  const handleFileUpload = async () => {
    if (files.length === 0) {
      setAlert({ type: 'error', content: 'Please select a file to upload' });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      console.log('Starting upload process...');
      
      // Skip credential test and go straight to upload
      for (const file of files) {
        console.log('Uploading file:', { name: file.name, size: file.size, type: file.type });
        
        const docId = uuidv4();
        const key = `uploads/user123/${docId}/${file.name}`;
        console.log('Upload key:', key);

        // Use putObject instead of upload for better CORS compatibility
        const params = {
          Bucket: config.bucketName,
          Key: key,
          Body: file,
          ContentType: file.type,
          Metadata: {
            docId: docId,
            userId: 'user123',
            originalName: file.name
          }
        };

        console.log('Starting putObject...');
        console.log('Upload params:', {
          Bucket: params.Bucket,
          Key: params.Key,
          ContentType: params.ContentType
        });
        
        const uploadResult = await s3.putObject(params).promise();
        console.log('Upload successful:', uploadResult);
        
        setUploadProgress(100);
      }

      setAlert({ type: 'success', content: 'File uploaded successfully!' });
      setFiles([]);
      setUploadProgress(0);
    } catch (error) {
      console.error('Detailed upload error:', {
        message: error.message,
        code: error.code,
        statusCode: error.statusCode,
        stack: error.stack
      });
      setAlert({ type: 'error', content: `Upload failed: ${error.message}` });
    } finally {
      setUploading(false);
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      setStream(mediaStream);
      setShowCamera(true);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      setAlert({ type: 'error', content: 'Camera access denied or not available' });
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setFiles([file]);
      stopCamera();
    }, 'image/jpeg', 0.8);
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Upload documents for processing with Textract"
        >
          Document Upload
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        {alert && (
          <Alert
            type={alert.type}
            dismissible
            onDismiss={() => setAlert(null)}
          >
            {alert.content}
          </Alert>
        )}

        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
          <Box>
            <SpaceBetween direction="vertical" size="m">
              <Header variant="h3">Upload File</Header>
              <FileUpload
                onChange={({ detail }) => setFiles(detail.value)}
                value={files}
                i18nStrings={{
                  uploadButtonText: e => e ? "Choose files" : "Choose file",
                  dropzoneText: e => e ? "Drop files to upload" : "Drop file to upload",
                  removeFileAriaLabel: e => `Remove file ${e + 1}`,
                  limitShowFewer: "Show fewer files",
                  limitShowMore: "Show more files",
                  errorIconAriaLabel: "Error"
                }}
                multiple={false}
                accept=".pdf,.png,.jpg,.jpeg"
                showFileLastModified
                showFileSize
                showFileThumbnail
                constraintText="Supported formats: PDF, PNG, JPG, JPEG. Max size: 10MB"
              />
            </SpaceBetween>
          </Box>

          <Box>
            <SpaceBetween direction="vertical" size="m">
              <Header variant="h3">Scan Document</Header>
              <Button
                variant="normal"
                iconName="camera"
                onClick={startCamera}
                disabled={uploading}
              >
                Open Camera
              </Button>
            </SpaceBetween>
          </Box>
        </Grid>

        {uploading && (
          <ProgressBar
            value={uploadProgress}
            label="Upload progress"
            description={`${uploadProgress}% complete`}
          />
        )}

        <Button
          variant="primary"
          onClick={handleFileUpload}
          disabled={files.length === 0 || uploading}
          loading={uploading}
        >
          Upload Document
        </Button>

        <Modal
          visible={showCamera}
          onDismiss={stopCamera}
          header="Capture Document"
          size="large"
        >
          <SpaceBetween direction="vertical" size="m">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{ width: '100%', maxHeight: '400px' }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <SpaceBetween direction="horizontal" size="s">
              <Button variant="primary" onClick={capturePhoto}>
                Capture
              </Button>
              <Button onClick={stopCamera}>
                Cancel
              </Button>
            </SpaceBetween>
          </SpaceBetween>
        </Modal>
      </SpaceBetween>
    </Container>
  );
}

export default DocumentUpload;